/**
 * Kitten — frantic baby cat NPC (Babylon.js port).
 *
 * Starts frantic, calmed by Minna's Purr. Once calmed, follows Minna
 * and can be guided to the kitten pen.
 *
 * Visual: small capsule with orange/warm tint. Distress indicated by a
 * pulsing emissive glow and a point light that flickers.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { NPC, NPCConfig } from "./NPC";
import { NPC_TUNING } from "../../config/GameConfig";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";

export class Kitten extends NPC {
  override get species(): string { return "kitten"; }

  private distressLight: PointLight;
  private _isPenned = false;

  constructor(scene: Scene, key: string, position: Vector3) {
    const config: NPCConfig = {
      key,
      position,
      height: 0.6,
      radius: 0.2,
      color: '#ffaa66',
      behavior: {
        initialState: 'frantic',
        calmRadius: NPC_TUNING.CALM_RADIUS,
        calmThreshold: NPC_TUNING.KITTEN.CALM_THRESHOLD,
        calmRate: NPC_TUNING.KITTEN.CALM_RATE,
        calmDecay: NPC_TUNING.KITTEN.CALM_DECAY,
        followSpeed: NPC_TUNING.KITTEN.FOLLOW_SPEED,
        followDistance: NPC_TUNING.KITTEN.FOLLOW_DISTANCE,
      },
      modelPath: "/assets/models/npcs/kitten.glb",
      modelScale: 3.0,
    };

    super(scene, config);

    // Distress point light — flickers orange when frantic
    this.distressLight = new PointLight(`kitten_${key}_distress`, Vector3.Zero(), scene);
    this.distressLight.diffuse = new Color3(1.0, 0.4, 0.15);
    this.distressLight.specular = Color3.Black();
    this.distressLight.intensity = 1.5;
    this.distressLight.range = 3;
    this.distressLight.parent = this.mesh;
    this.distressLight.position = new Vector3(0, 0.5, 0);
  }

  override update(time: number, _deltaSeconds: number): void {
    // Distress light only when frantic
    this.distressLight.setEnabled(this.behavior.state === 'frantic');

    if (this.behavior.state === 'frantic') {
      // Flicker intensity
      this.distressLight.intensity = 1.2 + Math.sin(time * 8) * 0.5;
    }

    // Tint changes
    switch (this.behavior.state) {
      case 'frantic':
        this.material.diffuseColor = Color3.FromHexString('#ffaa66');
        this.material.emissiveColor = new Color3(0.3, 0.15, 0.05);
        break;
      case 'calming':
        this.material.diffuseColor = Color3.FromHexString('#ddcc88');
        this.material.emissiveColor = new Color3(0.15, 0.2, 0.05);
        break;
      case 'calmed':
      case 'following':
        this.material.diffuseColor = Color3.FromHexString('#eeddaa');
        this.material.emissiveColor = Color3.Black();
        break;
    }
  }

  /** Mark this kitten as penned (inside the kitten pen). */
  pen(): void {
    this._isPenned = true;
    this.behavior.setState('calmed');
    // Stop all movement
    this.mesh.position.x += 0; // no-op, just ensuring no velocity carry-over
    gameEvents.emit(GameEvents.KITTEN_PENNED, { npcKey: this.npcKey });
  }

  get isPenned(): boolean {
    return this._isPenned;
  }

  override dispose(): void {
    this.distressLight.dispose();
    super.dispose();
  }
}
